import React, { useEffect, useState, useCallback } from "react";
import styles from "./AuthModal.module.scss";
import classNames from "classnames/bind";
import { useOpenCloseAuthModalStore } from "../../stores/Auth/useAuthModal";
import useAuthStore from "../../stores/Auth/AuthStore";
import { FORM_MODES } from "../../config/authModalConfig/constants";
import { useFormConfig } from "../../hooks/useFormConfig";
import { useFormValidation } from "../../hooks/useFormValidation";
import { useAuthSubmit } from "../../hooks/useAuthSubmit";
import ModalHeader from "./components/ModalHeader";
import AuthForm from "./components/AuthForm";
import useTranslations from "../../hooks/useTranslations";

const cx = classNames.bind(styles);

const AuthModal = () => {
  const { isOpen, close, mode } = useOpenCloseAuthModalStore();
  const { error, clearError } = useAuthStore();
  const currentTranslations = useTranslations()
  // State for current form mode
  const [modeForm, setModeForm] = useState(FORM_MODES.LOGIN);
  const [rememberLogIn, setRememberLogIn] = useState(false);

  // Get form configuration based on current mode
  const formConfig = useFormConfig(modeForm, currentTranslations);

  // Form validation hook
  const {
    formData,
    errors,
    touched,
    handleInputChange,
    handleInputBlur,
    validateForm,
    isFormValid: formIsValid,
    resetForm,
    setAllTouched,
  } = useFormValidation(formConfig, modeForm);

  // Auth submission hook
  const { handleSubmit: submitAuth, isLoading } = useAuthSubmit(
    modeForm,
    close,
    setModeForm
  );

  // Set initial mode from store when modal opens
  useEffect(() => {
    if (isOpen && mode) {
      setModeForm(mode);
    } else if (isOpen && !mode) {
      setModeForm(FORM_MODES.LOGIN);
    }
  }, [isOpen, mode]);

  // Reset form and clear errors when switching modes
  useEffect(() => {
    resetForm();
    clearError();
  }, [modeForm, resetForm, clearError]);

  // Handle mode change
  const handleChangeMode = useCallback((mode) => {
    setModeForm(mode);
  }, []);

  // Handle form submission
  const handleSubmit = useCallback(async () => {
    setAllTouched();

    if (validateForm()) {
      await submitAuth(formData, rememberLogIn, resetForm);
    } 
  }, [
    validateForm,
    submitAuth,
    formData,
    rememberLogIn,
    resetForm,
    setAllTouched,
  ]);

  // Handle input change with error clearing
  const handleInputChangeWithErrorClear = useCallback(
    (fieldName, value) => {
      handleInputChange(fieldName, value);
      if (error) {
        clearError();
      }
    },
    [handleInputChange, error, clearError]
  );

  // Handle click outside modal to close
  const handleOverlayClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget) {
        close();
        setModeForm(FORM_MODES.LOGIN);
      }
    },
    [close]
  );

  if (!isOpen) return null;

  return (
    <div
      className={cx("container__authModal", { "authModal-open": isOpen })}
      onClick={handleOverlayClick}
    >
      <div
        className={cx("modal__content", {
          modal_register: modeForm === FORM_MODES.REGISTER,
        })}
      >
        <ModalHeader title={formConfig.title} onClose={close} />

        <AuthForm
          formConfig={formConfig}
          modeForm={modeForm}
          formData={formData}
          errors={errors}
          touched={touched}
          error={error}
          rememberLogIn={rememberLogIn}
          setRememberLogIn={setRememberLogIn}
          isFormValid={formIsValid()}
          isLoading={isLoading}
          onInputChange={handleInputChangeWithErrorClear}
          onInputBlur={handleInputBlur}
          onSubmit={handleSubmit}
          onChangeMode={handleChangeMode}
        />
      </div>
    </div>
  );
};

export default AuthModal;
